import React, { useState } from "react";
import { MapPin, Mail, Phone } from "lucide-react";
import "../styles/components/footer.css";
import { ReactComponent as LogoSvg } from "../assets/icons/Logo.svg";
import { TermsService } from "../services/termsService";
import { PrivacyPolicyService } from "../services/privacyPolicyService";
import { toAbsoluteUrl } from "../config/api";

const Footer: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{
    title?: string;
    content?: string;
    fileUrl?: string;
  } | null>(null);

  const handleTermsClick = async () => {
    try {
      const data = await TermsService.getActiveTerms();
      if (data) {
        const fileUrl = data.filePath ? toAbsoluteUrl(data.filePath) : undefined;
        
        // Ưu tiên hiển thị file nếu có
        if (fileUrl) {
          window.open(fileUrl, "_blank", "noopener");
          return;
        }
        
        // Nếu không có file, hiển thị modal với nội dung
        setModalContent({
          title: data.title,
          content: data.content || "Nội dung điều khoản chưa được cập nhật.",
        });
        setIsModalOpen(true);
      } else {
        setModalContent({
          title: "Điều khoản",
          content: "Hiện chưa có điều khoản hoạt động.",
        });
        setIsModalOpen(true);
      }
    } catch (err) {
      setModalContent({
        title: "Điều khoản",
        content:
          err instanceof Error
            ? err.message
            : "Không thể tải điều khoản.",
      });
      setIsModalOpen(true);
    }
  };

  const handlePrivacyPolicyClick = async () => {
    try {
      const data = await PrivacyPolicyService.getActivePrivacyPolicy();
      if (data) {
        const fileUrl = data.filePath ? toAbsoluteUrl(data.filePath) : undefined;
        
        // Ưu tiên hiển thị file nếu có
        if (fileUrl) {
          window.open(fileUrl, "_blank", "noopener");
          return;
        }
        
        // Nếu không có file, hiển thị modal với nội dung
        setModalContent({
          title: data.title,
          content: data.content || "Nội dung chính sách bảo mật chưa được cập nhật.",
        });
        setIsModalOpen(true);
      } else {
        setModalContent({
          title: "Chính sách bảo mật",
          content: "Hiện chưa có chính sách bảo mật hoạt động.",
        });
        setIsModalOpen(true);
      }
    } catch (err) {
      setModalContent({
        title: "Chính sách bảo mật",
        content:
          err instanceof Error
            ? err.message
            : "Không thể tải chính sách bảo mật.",
      });
      setIsModalOpen(true);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-logo">
        <LogoSvg
          style={{ width: "300", height: "100", marginBottom: "2rem" }}
        />
        <p>
          Chúng tôi luôn sẵn sàng hỗ trợ. Hãy liên hệ với chúng tôi để tìm giải
          pháp tối ưu nhất cho vấn đề của bạn.
        </p>
      </div>
      <div className="footer-column footer-contact">
        <h3 className="footer-title">Liên hệ với chúng tôi</h3>
        <div className="footer-contact-item">
          <MapPin color="currentColor" size={24} />
          <a
            href="https://www.google.com/maps/search/?api=1&query=194+đường+số+7,+Khu+dân+cư+Trung+Sơn,+xã+Bình+Hưng,+huyện+Bình+Chánh,+TPHCM"
            className="footer-contact-link"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Xem vị trí công ty trên Google Maps"
          >
            194 đường số 7, Khu dân cư Trung Sơn, xã Bình Hưng, huyện Bình
            Chánh, TPHCM
          </a>
        </div>
        <div className="footer-contact-item">
          <Mail color="currentColor" size={24} />
          <a
            href="mailto:congngheorongbien@gmail.com"
            className="footer-contact-link"
          >
            Email: congngherongbien@gmail.com
          </a>
        </div>
        <div className="footer-contact-item">
          <Phone color="currentColor" size={24} />
          <a href="tel:0931494389" className="footer-contact-link">
            Hotline: 0931494389
          </a>
        </div>
      </div>
      <div className="footer-column">
        <h3 className="footer-title">Chính sách</h3>
        <button
          type="button"
          onClick={handlePrivacyPolicyClick}
          style={{
            background: "transparent",
            border: "none",
            color: "inherit",
            textDecoration: "none",
            cursor: "pointer",
            padding: 0,
            margin: "4px 0",
            fontSize: "inherit",
            fontFamily: "inherit",
            textAlign: "left",
            width: "100%",
          }}
        >
          Chính sách bảo mật
        </button>
        <button
          type="button"
          onClick={handleTermsClick}
          style={{
            background: "transparent",
            border: "none",
            color: "inherit",
            textDecoration: "none",
            cursor: "pointer",
            padding: 0,
            margin: "4px 0",
            fontSize: "inherit",
            fontFamily: "inherit",
            textAlign: "left",
            width: "100%",
          }}
        >
          Điều khoản sử dụng
        </button>
      </div>
      <div className="footer-column footer-map">
        <h3 className="footer-title">Liên hệ với chúng tôi</h3>
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3547.410174818486!2d106.68744887451673!3d10.733260859989516!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31752fbaee249739%3A0x4485210532f2e667!2zMTk0IMSQLiBz4buRIDcsIEtodSBkw6FuIGPGsCBUcnVuZyBTxqFuLCBCw6xuaCBDaMOhbmgsIEjhu5MgQ2jDrSBNaW5oLCBWaeG7h3QgTmFt!5e1!3m2!1svi!2s!4v1745833490448!5m2!1svi!2s"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Bản đồ vị trí công ty SDTC"
          ></iframe>
        </div>
      </div>
      {isModalOpen && (
        <div
          id="policy-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="policy-modal-title"
          className="help-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="help-modal"
            style={{
              width: "min(720px, 94vw)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              padding: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <h3 id="policy-modal-title" style={{ margin: 0 }}>
                {modalContent?.title || "Chính sách"}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Đóng chính sách"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                maxHeight: "60vh",
                overflowY: "auto",
                paddingRight: 6,
                color: "#333",
                fontSize: 14,
              }}
            >
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "inherit",
                  margin: 0,
                }}
              >
                {modalContent?.content || ""}
              </pre>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 16,
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="action-btn"
              >
                Tôi đã đọc
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
